// src/components/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa'; // Import eye icons for password toggle

const API_BASE = process.env.REACT_APP_API_URL;

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false); // State for password visibility
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from;

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userRole = localStorage.getItem('userRole');
    console.log('Login.js useEffect: Initial check - token:', token ? 'exists' : 'null', 'userRole:', userRole);

    if (token) {
        if (userRole === 'admin' && from && from.startsWith('/admin')) {
            console.log('Login.js useEffect: Redirecting admin to intended path:', from);
            navigate(from, { replace: true });
        } else if (userRole === 'admin') {
            console.log('Login.js useEffect: Redirecting admin to dashboard.');
            navigate('/admin/dashboard', { replace: true });
        } else if (userRole) { // For any other non-null role (assumed to be a regular user)
            console.log('Login.js useEffect: Redirecting regular user to user dashboard.');
            navigate('/user-dashboard', { replace: true });
        } else { // Token exists but role is null/undefined (e.g., corrupted local storage)
            console.warn('Login.js useEffect: Token found but userRole is null/undefined. Clearing storage and redirecting to login.');
            localStorage.removeItem('token');
            localStorage.removeItem('userRole');
            navigate('/login', { replace: true });
        }
    }
  }, [navigate, from]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    console.log('--- Frontend Login Attempt DEBUG ---');
    console.log(`Attempting login for username: '${username}'`);

    try {
      const res = await axios.post(`${API_BASE}/api/auth/login`, { name: username, password });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('userRole', res.data.role);
      console.log('Login.js handleSubmit: Token SET:', localStorage.getItem('token') ? 'YES' : 'NO');
      console.log('Login.js handleSubmit: User Role SET:', localStorage.getItem('userRole'));

      setMessage('✅ Login successful!');

      let redirectPath = '/';

      if (res.data.role === 'admin') {
        if (from && from.startsWith('/admin')) {
          redirectPath = from;
        } else {
          redirectPath = '/admin/dashboard';
        }
        console.log('Login.js handleSubmit: User is Admin, redirecting to:', redirectPath);
      } else {
        redirectPath = '/user-dashboard';
        console.log('Login.js handleSubmit: User is Regular User, redirecting to:', redirectPath);
      }

      navigate(redirectPath, { replace: true });

    } catch (err) {
      console.error('Login failed (frontend catch block):', err.response?.data || err.message, err.response?.status);
      let errorMessage = '❌ Login failed. Please check your credentials.';

      if (err.response?.status === 401) {
        console.warn('Login failed with 401. Clearing potentially stale token/userRole from localStorage.');
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        errorMessage = '❌ Login failed. Invalid username or password.';
      } else if (err.response && err.response.data && err.response.data.message) {
        errorMessage = `❌ Login failed: ${err.response.data.message}`;
      } else if (err.response && err.response.data && err.response.data.errors && err.response.data.errors.length > 0) {
        errorMessage = `❌ Login failed: ${err.response.data.errors[0].msg || 'Validation error'}`;
      } else if (err.message) {
        errorMessage = `❌ Login failed: ${err.message}`;
      }
      setMessage(errorMessage);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-cyan-600 to-blue-800 font-inter text-white">
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md bg-opacity-10 backdrop-filter backdrop-blur-lg border border-white/20">
        <h2 className="text-3xl font-bold mb-6 text-center text-white">Login to SK Paints</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-white text-sm font-bold mb-2" htmlFor="username">
              Username
            </label>
            <input
              type="text"
              id="username"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-white/20 border-white/30 placeholder-gray-300"
              required
            />
          </div>
          <div className="relative">
            <label className="block text-white text-sm font-bold mb-2" htmlFor="password">
              Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="shadow appearance-none border rounded w-full py-3 px-4 pr-10 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-white/20 border-white/30 placeholder-gray-300"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center pt-8 text-gray-300 hover:text-white focus:outline-none"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          <button
            type="submit"
            className="bg-white text-cyan-700 font-bold py-2 px-4 rounded-full w-full hover:bg-gray-200 transition duration-300"
          >
            Login
          </button>
        </form>
        {message && (
          <p className={`mt-4 text-center ${message.startsWith('✅') ? 'text-green-300' : 'text-red-300'}`}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default Login;
